import React, { memo, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { OEContext } from '@aytm/app/stats/page/comments'

import {
  DEFAULT_SELECTED_QUESTION,
  CORRECTIONS,
  ORIGINALS,
  CORRECTED_SPELLING
} from '@aytm/app/stats/page/comments/lib/constants'

import ClickOutside from '@aytm/lib/rfd-ui/components/basic/click-outside'

import { Checkbox } from 'primereact/checkbox'
import { RadioButton } from 'primereact/radiobutton'

import Dropdown from '@aytm/lib/rfd-ui/components/basic/dropdown'

import style from '@aytm/app/stats/page/comments/styles/dropdown.module.scss'

const SurveyDropdown = memo(
  ({
    questionsList,
    responseOrderRef,
    opened,
    hasQuestionOrder,
    isAdvancedFiltersDisabled,
    onNewFilter,
    onToggleQAMode,
    onClickOutside
  }) => {
    const { searchState, dispatch } = useContext(OEContext)

    const {
      request: { selectedQuestion, query },
      correctionsLevel,
      totalComments,
      totalImageResponses,
      totalVideoResponses,
      isHighlightSearchTerms,
      isResponseOrder,
      isSentimentsManagementDisabled,
      isShowCorrectionsDisabled,
      isShowSentiments,
      isQAModeDisabled,
      isVideoResponsesMode
    } = searchState

    const question = useMemo(
      () => questionsList.find(q => q.value == selectedQuestion) || null,
      [questionsList, selectedQuestion]
    )

    const isCreateFilterDisabled = useMemo(
      () =>
        (!query.length && selectedQuestion == DEFAULT_SELECTED_QUESTION) ||
        query.some(item => 'batch' == item.type) ||
        false,
      [query, selectedQuestion]
    )

    const onToggleCorrectionLevel = level =>
      dispatch({ type: 'SET_PARAM', request: { name: 'correctionsLevel', value: level } })

    const onToggleResponseOrder = isResponseOrder => {
      dispatch({ type: 'SET_PARAM', request: { name: 'isResponseOrder', value: isResponseOrder } })
      Object.keys(responseOrderRef.current).forEach(key => {
        responseOrderRef.current[key](isResponseOrder)
      })
    }

    const onToggleHighlightSearchTerms = () =>
      dispatch({ type: 'SET_PARAM', request: { name: 'isHighlightSearchTerms', value: !isHighlightSearchTerms } })

    const onToggleShowSentiment = () =>
      dispatch({
        type: 'SET_PARAM',
        request: { name: 'isShowSentiments', value: !isShowSentiments }
      })

    return (
      <Dropdown
        data-testid="stat-comments-settings"
        className={style['settings-dropdown']}
        width="auto"
        align="right"
        opened={opened}
      >
        {() => (
          <ClickOutside onClickOutside={() => setTimeout(() => onClickOutside())}>
            <ul className={`${style['settings-dropdown-options']} p-mod-small`}>
              {isShowCorrectionsDisabled ? null : (
                <>
                  <li
                    data-testid="stat-comments-show-originals"
                    className={style['settings-dropdown-option']}
                    onClick={() => onToggleCorrectionLevel(ORIGINALS)}
                  >
                    <RadioButton checked={correctionsLevel == ORIGINALS} />
                    <span className={style['settings-dropdown-option-label']}>Originals</span>
                  </li>
                  <li
                    data-testid="stat-comments-show-corrections"
                    className={style['settings-dropdown-option']}
                    onClick={() => onToggleCorrectionLevel(CORRECTIONS)}
                  >
                    <RadioButton checked={correctionsLevel == CORRECTIONS} />
                    <span className={style['settings-dropdown-option-label']}>Show corrections</span>
                  </li>
                  <li
                    data-testid="stat-comments-corrected-spelling"
                    className={style['settings-dropdown-option']}
                    onClick={() => onToggleCorrectionLevel(CORRECTED_SPELLING)}
                  >
                    <RadioButton checked={correctionsLevel == CORRECTED_SPELLING} />
                    <span className={style['settings-dropdown-option-label']}>Corrected spelling</span>
                  </li>
                </>
              )}
              {selectedQuestion == DEFAULT_SELECTED_QUESTION && hasQuestionOrder ? (
                <>
                  <li
                    data-testid="stat-comments-qs-survey-order"
                    className={classNames(style['settings-dropdown-option'], {
                      [style['settings-dropdown-option-disabled']]: !hasQuestionOrder
                    })}
                    onClick={hasQuestionOrder ? () => onToggleResponseOrder(false) : null}
                  >
                    <RadioButton checked={!isResponseOrder} />
                    <span className={style['settings-dropdown-option-label']}>Qs in survey order</span>
                  </li>
                  <li
                    data-testid="stat-comments-qs-response-order"
                    className={classNames(style['settings-dropdown-option'], {
                      [style['settings-dropdown-option-disabled']]: !hasQuestionOrder
                    })}
                    onClick={hasQuestionOrder ? () => onToggleResponseOrder(true) : null}
                  >
                    <RadioButton checked={isResponseOrder} />
                    <span className={style['settings-dropdown-option-label']}>Qs in response order</span>
                  </li>
                </>
              ) : null}
              <li
                data-testid="stat-comments-highlight-terms"
                className={style['settings-dropdown-option']}
                onClick={onToggleHighlightSearchTerms}
              >
                <Checkbox checked={isHighlightSearchTerms} />
                <span className={style['settings-dropdown-option-label']}>Highlight search terms</span>
              </li>
              {isSentimentsManagementDisabled ? null : (
                <li
                  data-testid="stat-comments-show-sentiments"
                  className={style['settings-dropdown-option']}
                  onClick={onToggleShowSentiment}
                >
                  <Checkbox checked={isShowSentiments} />
                  <span className={style['settings-dropdown-option-label']}>Show sentiment</span>
                </li>
              )}
              {(null === question || !['ir', 'va', 'vr'].includes(question.type)) && !isAdvancedFiltersDisabled ? (
                <li
                  data-testid="stat-comments-create-filter"
                  className={classNames(style['settings-dropdown-option'], {
                    [style['settings-dropdown-option-disabled']]: isCreateFilterDisabled
                  })}
                  onClick={isCreateFilterDisabled ? null : onNewFilter}
                >
                  <i className="icon-aytmfd-filter-v2" />
                  <span className={style['settings-dropdown-option-label']}>Create filter</span>
                </li>
              ) : null}
              {!isQAModeDisabled && !isVideoResponsesMode ? (
                <li
                  data-testid="stat-comments-qa-mode"
                  className={style['settings-dropdown-option']}
                  onClick={onToggleQAMode}
                >
                  <i className="icon-aytmfd-qa-mode" />
                  <span className={style['settings-dropdown-option-label']}>Enter QA mode</span>
                </li>
              ) : null}
              <li className={style['settings-dropdown-footer']}>
                <span>
                  {totalComments ? (
                    <>
                      <i className="icon-aytmfd-comments-v2" />
                      {totalComments}
                    </>
                  ) : null}
                </span>
                <span>
                  {totalImageResponses ? (
                    <>
                      <i className="icon-aytmfd-image-responses-v2" />
                      {totalImageResponses}
                    </>
                  ) : null}
                </span>
                <span>
                  {totalVideoResponses ? (
                    <>
                      <i className="icon-aytmfd-video-responses-v2" />
                      {totalVideoResponses}
                    </>
                  ) : null}
                </span>
              </li>
            </ul>
          </ClickOutside>
        )}
      </Dropdown>
    )
  }
)

SurveyDropdown.propTypes = {
  questionsList: PropTypes.array,
  responseOrderRef: PropTypes.object,
  opened: PropTypes.bool,
  hasQuestionOrder: PropTypes.bool,
  isAdvancedFiltersDisabled: PropTypes.bool,
  onNewFilter: PropTypes.func,
  onToggleQAMode: PropTypes.func,
  onClickOutside: PropTypes.func
}

SurveyDropdown.displayName = 'SurveyDropdown'

export default SurveyDropdown
